import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiGrid, FiCalendar, FiBarChart2, FiSidebar, FiLogOut, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const EXPANDED_WIDTH = 250;
const COLLAPSED_WIDTH = 72;

const StudentSideNav = ({ isOpen, collapsed, onToggleCollapse }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [logoHovered, setLogoHovered] = useState(false);
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => { logout(); navigate('/login'); };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navItems = [
        { to: '/student/dashboard', icon: <FiGrid />, label: 'Home' },
        { to: '/student/exams', icon: <FiCalendar />, label: 'Exams' },
        { to: '/student/results', icon: <FiBarChart2 />, label: 'Results' },
        { to: '/student/profile', icon: <FiUser />, label: 'Profile' },
    ];

    /* ── Mobile Bottom Bar ── */
    if (isMobile) {
        return (
            <nav style={{
                position: 'fixed', bottom: 0, left: 0, right: 0, height: 64,
                background: 'rgba(237,250,244,0.97)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderTop: '1px solid rgba(39,160,101,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-around',
                zIndex: 1000,
                boxShadow: '0 -4px 20px rgba(39,160,101,0.10)',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}>
                {navItems.map(item => (
                    <NavLink key={item.to} to={item.to}
                        style={({ isActive }) => ({
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                            textDecoration: 'none',
                            color: isActive ? '#27a065' : '#8ab5a0',
                            fontSize: 10, fontWeight: isActive ? 700 : 500, fontFamily: 'Outfit, sans-serif',
                            padding: '6px 12px', borderRadius: 14,
                            background: isActive ? 'rgba(39,160,101,0.10)' : 'transparent',
                            transition: 'all 0.2s ease',
                        })}
                    >
                        <span style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</span>
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </nav>
        );
    }

    const currentWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

    return (
        <aside style={{
            position: 'fixed', top: 0, left: 0, bottom: 0, width: currentWidth,
            background: 'linear-gradient(180deg, #d5f3d8 0%, #b8ecc8 55%, #edfaf4 100%)',
            display: 'flex', flexDirection: 'column', zIndex: 999,
            boxShadow: '4px 0 28px rgba(39,160,101,0.12)',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            borderRight: '1px solid rgba(39,160,101,0.15)',
        }}>
            {/* Header */}
            <div style={{
                padding: collapsed ? '18px 10px' : '18px 20px',
                borderBottom: '1px solid rgba(39,160,101,0.18)',
                transition: 'padding 0.3s ease',
            }}>
                {collapsed ? (
                    <div
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}
                        onMouseEnter={() => setLogoHovered(true)}
                        onMouseLeave={() => setLogoHovered(false)}
                    >
                        <div onClick={onToggleCollapse} style={{
                            width: 44, height: 44, borderRadius: 14,
                            background: 'linear-gradient(135deg, #6edba8, #27a065)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', position: 'relative', transition: 'all 0.25s ease',
                            boxShadow: '0 4px 14px rgba(39,160,101,0.28)',
                            overflow: 'hidden',
                        }}>
                            <span style={{ opacity: logoHovered ? 0 : 1, transition: 'opacity 0.2s ease', position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <img src="/svu-logo.png" alt="SVU" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }} />
                            </span>
                            <span style={{ opacity: logoHovered ? 1 : 0, transition: 'opacity 0.2s ease', color: 'white', position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FiSidebar size={16} />
                            </span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg, #6edba8, #27a065)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 14px rgba(39,160,101,0.25)' }}>
                                <img src="/svu-logo.png" alt="SVU" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 14 }} />
                            </div>
                            <button onClick={onToggleCollapse} style={{ width: 32, height: 32, borderRadius: 10, border: 'none', background: 'rgba(39,160,101,0.12)', color: 'rgba(26,51,38,0.55)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, transition: 'all 0.2s ease' }}>
                                <FiSidebar size={15} />
                            </button>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <h2 style={{ color: '#1a3326', fontSize: 13, fontWeight: 800, fontFamily: 'Outfit, sans-serif', margin: 0, lineHeight: 1.35 }}>
                                Swami Vivekananda<br />University
                            </h2>
                            <p style={{ color: '#27a065', fontSize: 11, margin: '5px 0 0', fontFamily: 'Outfit, sans-serif', fontWeight: 600, letterSpacing: 0.4 }}>
                                Student Portal
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* Navigation */}
            <nav style={{ flex: 1, padding: collapsed ? '16px 8px' : '16px 12px', overflowY: 'auto', transition: 'padding 0.3s ease' }}>
                {!collapsed && (
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.8, color: 'rgba(26,51,38,0.38)', padding: '6px 12px 8px', fontFamily: 'Outfit, sans-serif' }}>
                        Menu
                    </div>
                )}
                {navItems.map(item => (
                    <NavLink key={item.to} to={item.to}
                        title={collapsed ? item.label : ''}
                        style={({ isActive }) => ({
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: collapsed ? '11px 0' : '11px 14px',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            borderRadius: 12, textDecoration: 'none', fontSize: 14, fontWeight: 600, marginBottom: 4,
                            fontFamily: 'Outfit, sans-serif', transition: 'all 0.2s ease',
                            color: isActive ? '#1e8c58' : 'rgba(26,51,38,0.65)',
                            background: isActive ? 'rgba(39,160,101,0.16)' : 'transparent',
                            boxShadow: isActive ? '0 2px 10px rgba(39,160,101,0.12)' : 'none',
                        })}
                    >
                        <span style={{ fontSize: collapsed ? 21 : 17, display: 'flex', minWidth: collapsed ? 'auto' : 17 }}>{item.icon}</span>
                        {!collapsed && <span>{item.label}</span>}
                        {!collapsed && <span style={{ marginLeft: 'auto', opacity: 0.3, fontSize: 12 }}>›</span>}
                    </NavLink>
                ))}
            </nav>



            {/* Logout */}
            <div style={{ padding: collapsed ? '10px 8px' : '10px 12px', borderTop: '1px solid rgba(39,160,101,0.15)' }}>
                <button onClick={handleLogout} title={collapsed ? 'Logout' : ''} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 10, padding: collapsed ? '11px 0' : '11px 14px', borderRadius: 12, border: 'none', background: 'rgba(239,68,68,0.07)', color: '#dc6060', cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'Outfit, sans-serif', transition: 'all 0.2s ease' }}>
                    <span style={{ fontSize: collapsed ? 21 : 17, display: 'flex' }}><FiLogOut /></span>
                    {!collapsed && <span>Logout</span>}
                </button>
            </div>

            {!collapsed && (
                <div style={{ padding: '8px 16px 10px', borderTop: '1px solid rgba(39,160,101,0.10)' }}>
                    <p style={{ fontSize: 10, color: 'rgba(26,51,38,0.30)', textAlign: 'center', fontFamily: 'Outfit, sans-serif', fontWeight: 500, margin: 0 }}>SVU Exam Portal v1.0</p>
                </div>
            )}
        </aside>
    );
};

export default StudentSideNav;
